/**
 * Content Calendar — month view with drag-to-reschedule for pending publish jobs.
 */
import React, { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Loader2, CalendarDays, Plus } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";

interface Job {
  job_id: string;
  caption: string;
  scheduled_for?: string;
  created_at: string;
  overall_status: string;
  targets: { platform: string; status: string }[];
}

const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const startOfMonthGrid = (d: Date) => {
  const first = new Date(d.getFullYear(), d.getMonth(), 1);
  const shift = (first.getDay() + 6) % 7; // Mon=0
  return new Date(first.getFullYear(), first.getMonth(), 1 - shift);
};

const isSameDay = (a: Date, b: Date) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

export const Calendar: React.FC = () => {
  const qc = useQueryClient();
  const [cursor, setCursor] = useState(new Date());
  const today = new Date();

  const jobs = useQuery<Job[]>({
    queryKey: ["calendar-jobs"],
    queryFn: async () => (await api.get("/publish")).data || [],
  });

  const reschedule = useMutation({
    mutationFn: async ({ job_id, scheduled_for }: { job_id: string; scheduled_for: string }) =>
      (await api.post(`/composer/reschedule/${job_id}`, { scheduled_for })).data,
    onSuccess: () => { toast.success("Rescheduled"); qc.invalidateQueries({ queryKey: ["calendar-jobs"] }); },
    onError: (e: any) => toast.error(e?.response?.data?.detail || "Reschedule failed"),
  });

  const cells = useMemo(() => {
    const startCal = startOfMonthGrid(cursor);
    const arr: Date[] = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(startCal);
      d.setDate(startCal.getDate() + i);
      arr.push(d);
    }
    return arr;
  }, [cursor]);

  const jobsByDay: Record<string, Job[]> = useMemo(() => {
    const map: Record<string, Job[]> = {};
    (jobs.data || []).forEach((j) => {
      const when = j.scheduled_for || j.created_at;
      if (!when) return;
      const d = new Date(when);
      const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      (map[key] = map[key] || []).push(j);
    });
    return map;
  }, [jobs.data]);

  const onDrop = (day: Date, ev: React.DragEvent) => {
    ev.preventDefault();
    const jobId = ev.dataTransfer.getData("text/plain");
    if (!jobId) return;
    const now = new Date(day);
    now.setHours(10, 0, 0, 0);
    reschedule.mutate({ job_id: jobId, scheduled_for: now.toISOString() });
  };

  const changeMonth = (delta: number) => {
    const c = new Date(cursor); c.setMonth(cursor.getMonth() + delta); setCursor(c);
  };

  const monthLabel = cursor.toLocaleString("en-US", { month: "long", year: "numeric" });

  const statusColor = (j: Job) => {
    if (j.overall_status === "published") return "bg-emerald-500";
    if (j.overall_status === "failed") return "bg-red-500";
    if (j.scheduled_for && new Date(j.scheduled_for) > new Date()) return "bg-[color:var(--sb-saffron)]";
    return "bg-slate-400";
  };

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto p-6" data-testid="calendar-page">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <h1 className="font-display text-3xl font-bold tracking-tight flex items-center gap-2">
              <CalendarDays className="h-6 w-6 text-[color:var(--sb-saffron)]" /> Content Calendar
            </h1>
            <p className="text-sm text-[color:var(--sg-muted)] mt-1">Drag any scheduled post to a new date. Click an empty day to compose.</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => changeMonth(-1)} data-testid="cal-prev-btn"><ChevronLeft className="h-4 w-4" /></Button>
            <div className="font-display font-bold text-lg min-w-[180px] text-center" data-testid="cal-month-label">{monthLabel}</div>
            <Button variant="outline" size="sm" onClick={() => changeMonth(1)} data-testid="cal-next-btn"><ChevronRight className="h-4 w-4" /></Button>
            <Button variant="outline" size="sm" onClick={() => setCursor(new Date())} data-testid="cal-today-btn">Today</Button>
            <Link to="/composer">
              <Button size="sm" className="bg-[color:var(--sb-saffron)] hover:bg-[color:var(--sb-saffron-hover)] text-white" data-testid="cal-compose-btn">
                <Plus className="h-4 w-4 mr-1" /> Compose
              </Button>
            </Link>
          </div>
        </div>

        <div className="sg-card overflow-hidden">
          <div className="grid grid-cols-7 border-b border-[color:var(--sg-border)]">
            {DAY_LABELS.map((d) => (
              <div key={d} className="p-2 text-[10px] uppercase tracking-wider text-[color:var(--sg-muted)] font-semibold text-center">{d}</div>
            ))}
          </div>
          {jobs.isLoading ? (
            <div className="p-12 text-center text-sm text-[color:var(--sg-muted)]"><Loader2 className="h-5 w-5 animate-spin inline mr-2" /> Loading…</div>
          ) : (
            <div className="grid grid-cols-7">
              {cells.map((d, i) => {
                const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
                const items = jobsByDay[key] || [];
                const inMonth = d.getMonth() === cursor.getMonth();
                const isToday = isSameDay(d, today);
                return (
                  <div
                    key={i}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => onDrop(d, e)}
                    data-testid={`cal-day-${d.toISOString().slice(0,10)}`}
                    className={`min-h-[110px] border-b border-r border-[color:var(--sg-border)] p-1.5 flex flex-col gap-1 ${
                      inMonth ? "bg-[color:var(--sg-card)]" : "bg-[color:var(--sg-muted-bg)]/40"
                    }`}
                  >
                    <div className={`text-[11px] font-semibold ${isToday ? "text-[color:var(--sb-saffron)]" : inMonth ? "text-[color:var(--sg-fg)]" : "text-[color:var(--sg-muted)]"}`}>
                      {d.getDate()}
                    </div>
                    {items.slice(0, 3).map((j) => (
                      <div
                        key={j.job_id}
                        draggable={j.overall_status !== "published"}
                        onDragStart={(e) => e.dataTransfer.setData("text/plain", j.job_id)}
                        title={j.caption}
                        data-testid={`cal-job-${j.job_id}`}
                        className={`text-[10px] rounded px-1.5 py-0.5 cursor-grab active:cursor-grabbing truncate text-white ${statusColor(j)}`}
                      >
                        {j.scheduled_for ? new Date(j.scheduled_for).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" }) + " · " : ""}
                        {j.caption || "(untitled)"}
                      </div>
                    ))}
                    {items.length > 3 && (
                      <div className="text-[10px] text-[color:var(--sg-muted)]">+{items.length - 3} more</div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="mt-4 text-xs text-[color:var(--sg-muted)] flex flex-wrap gap-4">
          <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-[color:var(--sb-saffron)]" /> Scheduled</span>
          <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-emerald-500" /> Published</span>
          <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-red-500" /> Failed</span>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Calendar;
